import { useEffect, useCallback } from "react"
import { useTypedDispatch, useTypedSelector } from "../../../app/store"
import {
  useCreateRoomMutation,
  useJoinRoomMutation,
  useLeaveRoomMutation,
  useProposeChoiceMutation,
  useCastVoteMutation,
  useUpdateParticipantStatusMutation
} from "../../../app/services/collaborationApi"
import { livekitService } from "../services/livekitService"
import type { Choice } from "../../simulation-decisions/types"
import {
  setCurrentRoom,
  setCurrentUserId,
  setConnectionStatus,
  addParticipant,
  updateParticipantStatus as updateParticipantStatusAction,
  addCollaborativeChoice,
  updateCollaborativeChoice,
  castVote as castVoteAction,
  openVotingModal
} from "../collaborationSlice"
import { addChoice } from "../../simulation/simulationSlice"
import {
  CreateRoomRequest,
  ParticipantStatus,
  Participant,
  CollaborativeChoice,
  Vote,
  VotingStatus
} from "../types"

export const useCollaboration = () => {
  const dispatch = useTypedDispatch()
  const collaborationState = useTypedSelector(state => state.collaborationSlice)
  const authState = useTypedSelector(state => state.authSlice)

  const [createRoom, { isLoading: isCreatingRoom }] = useCreateRoomMutation()
  const [joinRoom, { isLoading: isJoiningRoom }] = useJoinRoomMutation()
  const [leaveRoom, { isLoading: isLeavingRoom }] = useLeaveRoomMutation()
  const [proposeChoice, { isLoading: isProposingChoice }] = useProposeChoiceMutation()
  const [castVote, { isLoading: isCastingVote }] = useCastVoteMutation()
  const [updateParticipantStatus] = useUpdateParticipantStatusMutation()

  // Helper to get effective user ID - supports both regular users and team simulation participants
  // Priority: 1) sessionStorage teamSimUserId (per-tab team simulation identity)
  //           2) collaborationState.currentUserId (set during room join)
  //           3) authState.user?.id (regular logged-in users)
  const getEffectiveUserId = useCallback((): number | null => {
    // Check sessionStorage first for team simulation user ID (per-tab isolation)
    try {
      const teamSimUserId = sessionStorage.getItem("teamSimUserId")
      if (teamSimUserId) {
        return parseInt(teamSimUserId, 10)
      }
    } catch {
      // Ignore sessionStorage errors
    }
    // Then check collaborationState (set for team simulation participants)
    if (collaborationState.currentUserId) {
      return collaborationState.currentUserId
    }
    // Fall back to auth state for regular users
    if (authState.user?.id) {
      return authState.user.id
    }
    return null
  }, [collaborationState.currentUserId, authState.user?.id])

  // Initialize user ID when auth state changes or from sessionStorage for team sim participants
  useEffect(() => {
    // For team sim, always prefer sessionStorage (per-tab) over persisted Redux state
    // This prevents cross-tab identity contamination when testing with multiple tabs
    try {
      const teamSimUserId = sessionStorage.getItem("teamSimUserId")
      if (teamSimUserId) {
        const userId = parseInt(teamSimUserId, 10)
        if (!isNaN(userId) && userId !== collaborationState.currentUserId) {
          dispatch(setCurrentUserId(userId))
        }
        return
      }
    } catch {
      // Ignore sessionStorage errors
    }

    if (collaborationState.currentUserId) {
      return // Already set
    }

    if (authState.user?.id) {
      dispatch(setCurrentUserId(authState.user.id))
    }
  }, [authState.user?.id, collaborationState.currentUserId, dispatch])

  // Set up LiveKit event listeners for collaboration events
  useEffect(() => {
    // Define handlers so we can remove them specifically on cleanup
    const handleParticipantJoined = (...args: unknown[]) => {
      const data = args[0] as Participant
      console.log('📥 [useCollaboration] participantJoined handler called with:', data)
      dispatch(addParticipant(data))
    }

    const handleParticipantStatusChanged = (...args: unknown[]) => {
      const data = args[0] as Record<string, unknown>
      console.log('📥 [useCollaboration] participantStatusChanged handler called with:', data)
      dispatch(updateParticipantStatusAction({
        participantId: data.participantId as string,
        status: data.status as ParticipantStatus
      }))
    }

    const handleChoiceProposed = (...args: unknown[]) => {
      console.log('📥 [useCollaboration] *** CHOICE PROPOSED HANDLER CALLED ***')
      console.log('📥 [useCollaboration] Raw args:', args)
      const data = args[0] as CollaborativeChoice
      console.log('📥 [useCollaboration] Parsed data:', data)
      console.log('📥 [useCollaboration] Data has id?', !!data?.id, 'id value:', data?.id)
      console.log('📥 [useCollaboration] Dispatching addCollaborativeChoice...')
      dispatch(addCollaborativeChoice(data))
      console.log('📥 [useCollaboration] Dispatching openVotingModal...')
      dispatch(openVotingModal(data))
      console.log('📥 [useCollaboration] Choice proposed handling complete!')
    }

    const handleVoteCast = (...args: unknown[]) => {
      console.log('📥 [useCollaboration] *** VOTE CAST HANDLER CALLED ***')
      console.log('📥 [useCollaboration] Raw args:', args)
      const data = args[0] as Record<string, unknown>
      console.log('📥 [useCollaboration] Parsed data:', data)

      // Construct a proper Vote object from the received data
      const vote: Vote = {
        participantId: data.participantId as string,
        decision: data.decision as 'approve' | 'reject' | 'abstain',
        timestamp: data.timestamp as string
      }

      console.log('📥 [useCollaboration] Constructed vote:', vote)
      console.log('📥 [useCollaboration] Dispatching castVoteAction with choiceId:', data.choiceId)
      dispatch(castVoteAction({
        choiceId: data.choiceId as string,
        vote
      }))
      console.log('📥 [useCollaboration] Vote cast handling complete!')
    }

    const handleChoiceResolved = (...args: unknown[]) => {
      const data = args[0] as CollaborativeChoice
      console.log('📥 [useCollaboration] choiceResolved handler called with:', data)
      dispatch(updateCollaborativeChoice(data))

      // EXECUTE APPROVED CHOICES: When a collaborative choice is approved,
      // dispatch it to the simulation engine to advance the simulation
      if (data.status === VotingStatus.APPROVED && data.choice) {
        console.log('✅ [useCollaboration] Choice APPROVED! Executing choice:', data.choice)
        dispatch(addChoice(data.choice))
        console.log('✅ [useCollaboration] Choice dispatched to simulation engine')
      }
    }

    // Register handlers
    console.log('🔧 [useCollaboration] === REGISTERING LIVEKIT EVENT HANDLERS ===')
    console.log('🔧 [useCollaboration] livekitService exists?', !!livekitService)
    console.log('🔧 [useCollaboration] isRoomConnected before registration:', livekitService.isRoomConnected())

    livekitService.on('participantJoined', handleParticipantJoined)
    livekitService.on('participantStatusChanged', handleParticipantStatusChanged)
    livekitService.on('choiceProposed', handleChoiceProposed)
    livekitService.on('voteCast', handleVoteCast)
    livekitService.on('choiceResolved', handleChoiceResolved)

    console.log('🔧 [useCollaboration] === HANDLERS REGISTERED SUCCESSFULLY ===')
    console.log('🔧 [useCollaboration] isRoomConnected after registration:', livekitService.isRoomConnected())

    // Cleanup: remove only our specific handlers, not all handlers for the event
    return () => {
      livekitService.off('participantJoined', handleParticipantJoined)
      livekitService.off('participantStatusChanged', handleParticipantStatusChanged)
      livekitService.off('choiceProposed', handleChoiceProposed)
      livekitService.off('voteCast', handleVoteCast)
      livekitService.off('choiceResolved', handleChoiceResolved)
    }
  }, [dispatch])

  const createCollaborativeRoom = useCallback(async (roomData: CreateRoomRequest) => {
    try {
      const response = await createRoom(roomData).unwrap()

      if (response.data) {
        dispatch(setCurrentRoom(response.data))
        dispatch(setConnectionStatus(livekitService.isRoomConnected()))

        return response.data
      }
    } catch (error) {
      console.error("Failed to create room:", error)
      throw error
    }
  }, [createRoom, dispatch])

  const joinCollaborativeRoom = useCallback(async (roomId: string) => {
    const userId = getEffectiveUserId()
    if (!userId) {
      throw new Error("User not authenticated")
    }

    try {
      const response = await joinRoom({
        roomId,
        userId
      }).unwrap()

      if (response.data?.room) {
        dispatch(setCurrentRoom(response.data.room))
        dispatch(setConnectionStatus(livekitService.isRoomConnected()))

        return response.data.room
      }
    } catch (error) {
      console.error("Failed to join room:", error)
      throw error
    }
  }, [joinRoom, dispatch, getEffectiveUserId])

  const leaveCollaborativeRoom = useCallback(async () => {
    const userId = getEffectiveUserId()
    if (!collaborationState.currentRoom || !userId) {
      return
    }

    try {
      await leaveRoom({
        roomId: collaborationState.currentRoom.id,
        participantId: userId.toString()
      }).unwrap()

      // Reset state (LiveKit room will be disconnected by component)
      dispatch(setCurrentRoom(null))
      dispatch(setConnectionStatus(false))
    } catch (error) {
      console.error("Failed to leave room:", error)
      throw error
    }
  }, [leaveRoom, collaborationState.currentRoom, getEffectiveUserId, dispatch])

  const proposeCollaborativeChoice = useCallback(async (choice: Choice) => {
    const userId = getEffectiveUserId()
    if (!collaborationState.currentRoom || !userId) {
      throw new Error("No active room or user not authenticated")
    }

    // Find the participant record for this user to get their participant UUID
    // The API expects the participant UUID (from collaborationParticipants.id), not the user ID
    const currentParticipant = collaborationState.participants.find(p => p.userId === userId)
    if (!currentParticipant) {
      console.error("Could not find participant record for user:", userId, "Available participants:", collaborationState.participants)
      throw new Error("Participant not found in room. Please try rejoining the simulation.")
    }

    try {
      const response = await proposeChoice({
        roomId: collaborationState.currentRoom.id,
        proposedBy: currentParticipant.id, // Use participant UUID, not user ID
        choice
      }).unwrap()

      // Add the proposed choice to Redux state and open voting modal
      // IMPORTANT: Merge the original choice (with full item data including responses)
      // into the response data, because the API only stores/returns minimal item data
      if (response.data) {
        const choiceWithFullData = {
          ...response.data,
          choice: choice  // Use the original choice with complete item data
        }
        dispatch(addCollaborativeChoice(choiceWithFullData))
        dispatch(openVotingModal(choiceWithFullData))

        // Broadcast the choice proposal via LiveKit so other participants see the voting modal
        // Use choiceWithFullData to ensure other participants get the complete item data
        try {
          livekitService.sendChoiceProposal(choiceWithFullData)
          console.log('📤 Broadcasted choice proposal via LiveKit:', choiceWithFullData.id)
        } catch (livekitError) {
          console.error('Failed to broadcast choice proposal via LiveKit:', livekitError)
        }
      }

      return response.data
    } catch (error) {
      console.error("Failed to propose choice:", error)
      throw error
    }
  }, [proposeChoice, collaborationState.currentRoom, collaborationState.participants, getEffectiveUserId, dispatch])

  const castCollaborativeVote = useCallback(async (
    choiceId: string,
    decision: 'approve' | 'reject' | 'abstain'
  ) => {
    const userId = getEffectiveUserId()
    if (!collaborationState.currentRoom || !userId) {
      throw new Error("No active room or user not authenticated")
    }

    // Find the participant record for this user to get their participant UUID
    const currentParticipant = collaborationState.participants.find(p => p.userId === userId)
    if (!currentParticipant) {
      console.error("Could not find participant record for user:", userId)
      throw new Error("Participant not found in room. Please try rejoining the simulation.")
    }

    try {
      const response = await castVote({
        roomId: collaborationState.currentRoom.id,
        choiceId,
        participantId: currentParticipant.id, // Use participant UUID, not user ID
        decision
      }).unwrap()

      // Broadcast vote via LiveKit data channel for real-time updates to other participants
      // Pass the participant UUID so other clients can properly match the vote
      livekitService.sendVote(choiceId, decision, currentParticipant.id)

      // Check if this vote resolved the choice (tipped the voting threshold)
      if (response.data?.resolved && response.data?.choice) {
        const resolvedChoice = response.data.choice
        console.log('🗳️ [useCollaboration] Vote resolved the choice! Status:', resolvedChoice.status)

        // Look up the FULL CollaborativeChoice from pendingChoices
        // The API only returns minimal choice data (id, text), but pendingChoices has the full item from proposal
        const fullChoice = collaborationState.pendingChoices.find(c => c.id === resolvedChoice.id)

        // Use the full choice data for broadcasting so other participants get complete item info
        const choiceTobroadcast = fullChoice ? { ...resolvedChoice, choice: fullChoice.choice } : resolvedChoice

        // Broadcast the resolved choice to other participants via LiveKit
        try {
          livekitService.sendChoiceResolution(choiceTobroadcast as unknown as Record<string, unknown>)
          console.log('📤 [useCollaboration] Broadcasted choice resolution to other participants')
        } catch (broadcastError) {
          console.error('Failed to broadcast choice resolution:', broadcastError)
        }

        // Update local Redux state with the resolved choice
        dispatch(updateCollaborativeChoice(resolvedChoice))

        // If the choice was APPROVED, execute it locally
        // (We won't receive our own LiveKit message, so we need to execute here)
        // Use the FULL choice from pendingChoices, not the minimal API response
        if (resolvedChoice.status === VotingStatus.APPROVED) {
          const choiceToExecute = fullChoice?.choice || resolvedChoice.choice

          if (choiceToExecute) {
            console.log('✅ [useCollaboration] Choice APPROVED by our vote! Executing locally:', choiceToExecute)
            dispatch(addChoice(choiceToExecute))
          } else {
            console.error('❌ [useCollaboration] No choice data found to execute!')
          }
        }
      }

      return response.data
    } catch (error) {
      console.error("Failed to cast vote:", error)
      throw error
    }
  }, [castVote, collaborationState.currentRoom, collaborationState.participants, collaborationState.pendingChoices, getEffectiveUserId, dispatch])

  const updateStatus = useCallback(async (status: ParticipantStatus) => {
    const userId = getEffectiveUserId()
    if (!collaborationState.currentRoom || !userId) {
      return
    }

    try {
      await updateParticipantStatus({
        roomId: collaborationState.currentRoom.id,
        participantId: userId.toString(),
        status
      }).unwrap()

      // Also broadcast via LiveKit data channel for real-time updates
      livekitService.sendParticipantStatusChange(status)
    } catch (error) {
      console.error("Failed to update participant status:", error)
    }
  }, [updateParticipantStatus, collaborationState.currentRoom, getEffectiveUserId])

  const isHost = useCallback(() => {
    const userId = getEffectiveUserId()
    return collaborationState.currentRoom?.createdBy === userId
  }, [collaborationState.currentRoom, getEffectiveUserId])

  const getCurrentParticipant = useCallback(() => {
    const userId = getEffectiveUserId()
    if (!userId) return null

    return collaborationState.participants.find(
      p => p.userId === userId
    )
  }, [collaborationState.participants, getEffectiveUserId])

  const canProposeChoices = useCallback(() => {
    const participant = getCurrentParticipant()
    return participant?.role === 'leader' || isHost()
  }, [getCurrentParticipant, isHost])

  // const resetState = useCallback(() => {
  //   // Reset the collaboration state when leaving or creating a new room
  //   dispatch(setCurrentRoom(null))
  //   dispatch(setConnectionStatus(false))
  // }, [dispatch])

  return {
    // State
    ...collaborationState,
    isHost: isHost(),
    currentParticipant: getCurrentParticipant(),
    canProposeChoices: canProposeChoices(),
    
    // Actions
    createRoom: createCollaborativeRoom,
    joinRoom: joinCollaborativeRoom,
    leaveRoom: leaveCollaborativeRoom,
    proposeChoice: proposeCollaborativeChoice,
    castVote: castCollaborativeVote,
    updateStatus,

    // Loading states
    isCreatingRoom,
    isJoiningRoom,
    isLeavingRoom,
    isProposingChoice,
    isCastingVote,
    
    // LiveKit connection status
    isLiveKitConnected: livekitService.isRoomConnected()
  }
} 